import { useEffect, useMemo, useState, useCallback } from "react";
import { collection, onSnapshot, orderBy, query, updateDoc, doc, getFirestore, getDocs } from "firebase/firestore";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthProvider";
import type { FirestoreTimestamp } from "../types/common";

export type Notif = {
  id: string;
  title: string;
  body: string;
  type: "reminder" | "cancel" | "info";
  sessionId?: string;
  createdAt: FirestoreTimestamp;
  readAt?: FirestoreTimestamp;
  href?: string;
};

export function useNotifications() {
  const { tenantId } = useApp();
  const { user } = useAuth();
  const db = getFirestore();

  const [items, setItems] = useState<Notif[]>([]);
  const unread = useMemo(() => items.filter((n) => !n.readAt).length, [items]);

  useEffect(() => {
    if (!tenantId || !user) return;
    const q = query(
      collection(db, "tenants", tenantId, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Notif[];
      setItems(arr);
    });
    return () => unsub();
  }, [tenantId, user, db]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!tenantId || !user) return;
      await updateDoc(doc(db, "tenants", tenantId, "users", user.uid, "notifications", id), { readAt: new Date() });
    },
    [tenantId, user, db]
  );

  // Evitamos where("readAt","==",null) porque muchos docs no traen el campo; filtramos en el cliente.
  const markAllAsRead = useCallback(async () => {
    if (!tenantId || !user) return;
    const q = query(
      collection(db, "tenants", tenantId, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    const updates = snap.docs.filter((d) => !d.data().readAt).map((d) => updateDoc(d.ref, { readAt: new Date() }));
    await Promise.all(updates);
  }, [tenantId, user, db]);

  return { items, unread, markAsRead, markAllAsRead };
}
